import dynamic from 'next/dynamic';
import { useRouter } from 'next/compat/router';
import React, { Fragment } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { useObserver } from 'mobx-react-lite';
import { GlobalStyles, EmbeddedStyles } from 'theme';
import { ThemeProvider } from 'theme/provider';
import { ZoomableImagePortalStyles } from 'components/zoomable-image';

import { useStores } from 'contexts/stores';
import GlobalControllers from 'components/global-controllers';
import EmbeddedEventsController from 'components/embedded-events-controller';
import { SeoMenu } from 'components/seo-menu';
import { IdleDetection } from 'src/components/idle-detection';
import Ernie from 'components/ernie';
import BackToTop from 'components/back-to-top';
import { ErrorFallback, ThirdPartyScriptBoundary } from 'src/errors';
import { useInitDataDogLogs } from 'src/hooks/use-init-datadog-logs';
import { Analytics } from 'utils/analytics';
import { LoadPerformanceMode } from 'src/components/load-performance-mode';
import { IncompleteAccountDetection } from 'src/components/incomplete-account-detection';
import { LoyaltyAccountDetection } from 'src/components/loyalty-account-detection';
import { PostSignupOAuthTrigger } from 'src/components/post-signup-oauth-trigger';
import { AdsInspectorDevTool } from 'src/adtech/inspector/ads-inspector-devtool';
import { ConsumerCatalogRequestProvider } from 'src/contexts/catalog-request-context';

// NOTE: GlobalModals is deprecated. we are in a process of refactoring to Modals
const GlobalModals = dynamic(() => import('src/components/modals/global-modals'), { ssr: false });
const Modals = dynamic(() => import('src/components/modals/modals').then((mod) => mod.Modals), { ssr: false });
const FidesController = dynamic(() => import('src/utils/fides').then((mod) => mod.FidesController), { ssr: false });

/**
 * Full consumer app shell. Wraps every standard marketplace page with
 * analytics, modals, theme, SEO, account detection, and other consumer chrome.
 *
 * Kept separate from the bare `BoardShell` so that board routes (TV menu
 * displays) can mount the lightest possible tree while still sharing the
 * always-on providers in `App` (_app.jsx).
 */
export function ConsumerShell({ Component, pageProps = {}, deviceType = 'desktop' }) {
  const { UI } = useStores();
  useInitDataDogLogs();

  const router = useRouter();
  const isEmbedded = useObserver(() => UI.isEmbedded);
  // `.layout` must never be assigned onto a shared page component (ECOMM-8974) — pages attach
  // layouts via withLayout(), which wraps instead of mutating. See with-layout.tsx.
  const Layout = Component.layout || Fragment;

  return (
    <ConsumerCatalogRequestProvider>
      <>
        <GlobalStyles />
        <ZoomableImagePortalStyles />
        {isEmbedded && <EmbeddedStyles />}
        <GlobalControllers />
        <FidesController />
        <ThemeProvider deviceType={deviceType}>
          <IdleDetection>
            <EmbeddedEventsController />
            <AdsInspectorDevTool />
            {/* Analytics sits outside the page ErrorBoundary below, so an error thrown
              here previously took down the whole React root. It loads
              dispensary-supplied third-party scripts (sGTM containers, etc.) whose
              bugs must not blank checkout. resetKeys matches the page boundary so a
              transient failure recovers on navigation instead of disabling analytics
              for the rest of the session. */}
            <ThirdPartyScriptBoundary name='analytics' resetKeys={[router?.asPath]}>
              <Analytics />
            </ThirdPartyScriptBoundary>
            <GlobalModals />
            <Modals />
            <PostSignupOAuthTrigger />
            <Ernie />
            {isEmbedded && <BackToTop />}
            <ErrorBoundary FallbackComponent={ErrorFallback} resetKeys={[router?.asPath]}>
              <SeoMenu>
                <IncompleteAccountDetection>
                  <LoyaltyAccountDetection>
                    <Layout {...(Layout === Fragment ? {} : pageProps)}>
                      <LoadPerformanceMode />
                      <Component {...pageProps} />
                    </Layout>
                  </LoyaltyAccountDetection>
                </IncompleteAccountDetection>
              </SeoMenu>
            </ErrorBoundary>
          </IdleDetection>
        </ThemeProvider>
      </>
    </ConsumerCatalogRequestProvider>
  );
}
